import React, { useEffect, useRef } from 'react';
import { Volume2 } from 'lucide-react';
import { SliderControl } from '../../timeline/components/toolbar/slider-control';
import { colors, dimensions, typography } from '../../theme';

type VolumeRowProps = {
  audioUrl: string;
  volume: number;
  onVolumeChange: (next: number) => void;
};

/**
 * Combina el `<audio controls />` de preview con un slider de volumen del editor.
 *
 * El slider es la fuente de verdad: al moverlo se escribe `audio.volume` del
 * elemento de preview y, al aplicar la clip al timeline, ese mismo valor pasa
 * al `gain` del `AudioClip` (ver `useApplyAudioClip`).
 *
 * El `<audio controls />` nativo expone su propio control de volumen; arrastrarlo
 * afecta solo a la reproducción local del preview y no rebota al slider del
 * panel. Acordamos en v1 que el slider del panel es el canon para apply.
 */
export const VolumeRow: React.FC<VolumeRowProps> = ({ audioUrl, volume, onVolumeChange }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  // Bind unidireccional slider → audio. Vuelve a correr si cambia la url del
  // audio (re-mount del elemento) para no quedar con el volumen default = 1.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume, audioUrl]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: dimensions.spacing.lg }}>
      <audio ref={audioRef} src={audioUrl} controls style={{ width: '100%' }} />
      <SliderControl
        value={volume}
        onChange={onVolumeChange}
        max={0.5}
        iconLeft={<Volume2 size={dimensions.size.iconSm} />}
        iconRight={
          <span
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              fontFamily: typography.fontFamily.ui,
              minWidth: 32,
              textAlign: 'right'
            }}
          >
            {Math.round(volume * 100)}%
          </span>
        }
        width={140}
        title='Volumen'
      />
    </div>
  );
};
